<template>
  <div v-if="isMobile && $route.fullPath === '/events' || !isMobile" class="events-container">
    <aside v-if="eventsStatus !== 'error'" class="events-aside">
      <div class="events-aside-navigation">
        <title-add
          :title="$t('CP.events')"
          modal="add-event"
          class="events-aside__title-add"/>
        <search-input
          :placeholder="$t('searchInput.placeholderSearch')"
          @search="onEventSearch"
        />
        <sort-list
          class="sort-list"
          @sortBy="(condition) => sortCondition = condition"
          :sort-condition="sortCondition"
          :sort-variants="sortVariants"
          @filterBy="(condition) => filterCondition = condition"
          :filter-condition="filterCondition"
          :filter-variants="filterVariants"
        />
      </div>
      <event-list
        class="event-list"
        :currency="eventsCurrency"
        :current-event-id="currentEventId"
        :current-event="event"
        :on-select-event="loadEvent"
        :events="filteredEvents"
        :events-status="eventsStatus"
        :onDeleteEvent="onDeleteEvent"
        :backlight="!isMobile"
        :route-id="routeId"
        :show-unread-messages="true"
        :no-events-phrase="noEventsPhrase"
        :event-date-mode="sortCondition.value"
      />
    </aside>
    <b-sidebar
      v-model="isEventsSidebarShown"
      ref="eventsSidebar"
      id="events-sidebar"
      no-header
      no-close-on-route-change
      sidebar-class="events-sidebar"
      backdrop
      shadow
    >
      <aside
        v-if="(isMobile && $route.fullPath === '/events'
          || !isMobile) && eventsStatus !== 'error'"
        class="events-aside_sidebar"
      >
        <div class="events-aside-navigation">
          <title-add
            modal="add-event"
            class="events-aside__title-add"/>
          <search-input
            @search="onEventSearch"
          />
          <sort-list
            class="sort-list"
            @sortBy="(condition) => sortCondition = condition"
            :sort-condition="sortCondition"
            :sort-variants="sortVariants"
            @filterBy="(condition) => filterCondition = condition"
            :filter-condition="filterCondition"
            :filter-variants="filterVariants"
          />
        </div>
        <event-list
          class="event-list"
          :currency="eventsCurrency"
          :current-event-id="currentEventId"
          :current-event="event"
          :on-select-event="loadEvent"
          :events="filteredEvents"
          :events-status="eventsStatus"
          :onDeleteEvent="onDeleteEvent"
          :backlight="!isMobile"
          :route-id="routeId"
          :show-unread-messages="true"
          :no-events-phrase="noEventsPhrase"
        />
      </aside>
    </b-sidebar>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Http } from '@/http/http';
import { SET_EVENT_CONTACTS } from '@/store/user/types';
import { productTours } from '@/plugins/shepherd';
import {
  CLEAR_CURRENT_EVENT,
  DELETE_EVENT_REQUEST,
  FETCH_EVENT_REQUEST,
  SET_CURRENT_EVENT,
} from '@/store/events/types';
import EventList from '../../components/EventList/EventList';
import SearchInput from '../../components/SearchInput/SearchInput';
import TitleAdd from '../../components/TitleAdd/TitleAdd';
import SortList from '../../components/SortList/SortList';

export default {
  name: 'EventsAside',
  components: {
    EventList,
    SearchInput,
    TitleAdd,
    SortList,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    routeId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      introShownOnce: false,
      isEventsSidebarShown: false,
      confirmModal: false,
      searchEvent: '',
      noEventsPhrase: 'eventList.noEvents',
      sortCondition: {
        text: this.$t('eventsAside.sortByUpdate'),
        value: 'byUpdate',
      },
      sortVariants: [
        {
          text: this.$t('eventsAside.sortByUpdate'),
          value: 'byUpdate',
        },
        {
          text: this.$t('eventsAside.sortByCreation'),
          value: 'byCreate',
        },
      ],
      filterCondition: {
        text: this.$t('eventsAside.filterAll'),
        value: 'All',
      },
      filterVariants: [
        {
          text: this.$t('eventsAside.filterAll'),
          value: 'All',
        },
        {
          text: this.$t('eventsAside.filterByDebts'),
          value: 'onlyDebts',
        },
      ],
      breakpointLg: window.matchMedia('(min-width: 1025px)'),
    };
  },
  watch: {
    locale() {
      this.sortVariants = [
        {
          text: this.$t('eventsAside.sortByUpdate'),
          value: 'byUpdate',
        },
        {
          text: this.$t('eventsAside.sortByCreation'),
          value: 'byCreate',
        },
      ];
      this.filterVariants = [
        {
          text: this.$t('eventsAside.filterAll'),
          value: 'All',
        },
        {
          text: this.$t('eventsAside.filterByDebts'),
          value: 'onlyDebts',
        },
      ];
      this.sortCondition = this.sortVariants
        .find((variant) => variant.value === this.sortCondition.value) || this.sortVariants[0];
      this.filterCondition = this.filterVariants
        .find((variant) => variant.value === this.filterCondition.value) || this.filterVariants[0];
    },
    filteredEvents(nextRes, prevRes) {
      const dontCloseSidebar = false;
      try {
        if (prevRes && prevRes.length && !this.isMobile) {
          const id = nextRes && nextRes[0] && nextRes[0].id;
          if ((prevRes && prevRes[0] && prevRes[0].id === id)) return;
          this.loadEvent(id, dontCloseSidebar);
        }
        if ((prevRes && (!prevRes[0] || !prevRes[0].id))
          && (nextRes && nextRes[0] && nextRes[0].id)
          && !this.isMobile && this.searchEvent) {
          this.loadEvent(nextRes[0].id, dontCloseSidebar);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('error in events watcher', e.message);
      }
    },
  },
  computed: {
    ...mapState({
      locale: (state) => state.app.currentLanguage,
      events: (state) => state.events.events || [],
      user: (state) => state.user.user,
      userStatus: (state) => state.user.userStatus,
      eventsCurrency: (state) => state.events.eventsCurrency,
      eventsStatus: (state) => state.events.eventsStatus,
      event: (state) => state.events.event,
      currentEventId: (state) => state.events.currentEventId,
      areAllToursFinished: (state) => state.tour.areAllToursFinished,
      tourStates: (state) => state.tour.tourStates,
    }),
    filteredEvents() {
      let events = [];
      if (this.events && this.events.length) {
        this.events.forEach((event) => { // filter by search string
          if (event && event.event_name) {
            const eventName = event.event_name
              .toLowerCase()
              .trim();
            const searchEvent = this.searchEvent
              .toLowerCase()
              .trim();
            if (eventName.indexOf(searchEvent) !== -1) {
              events.push(event);
            }
          }
          if (event && !event.event_name && event.contacts) {
            const quasiContact = event.contacts
              .find((contact) => contact.uid !== this.user.user);
            let eventName = '';
            if (quasiContact) {
              eventName = this.$getUserName(quasiContact);
            }
            // eslint-disable-next-line no-param-reassign
            event.quasi_event_name = eventName;
            const searchEvent = this.searchEvent
              .toLowerCase()
              .trim();
            if (event.quasi_event_name && event.quasi_event_name
              .toLowerCase()
              .trim()
              .indexOf(searchEvent) !== -1) {
              events.push(event);
            }
          }
        });
        if (this.filterCondition.value === 'onlyDebts') { // filter by a condition
          events = events.filter((event) => event.balance !== 0);
        }
        if (this.sortCondition.value === 'byCreate') { // sort by date
          // eslint-disable-next-line arrow-body-style
          events.sort((firstEvent, secondEvent) => {
            return +this.$moment(secondEvent.cr_date) - +this.$moment(firstEvent.cr_date);
          });
        }
      }
      if (!events.length && this.events.length) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.noEventsPhrase = 'eventList.noEventsFound';
        this.$store.commit(CLEAR_CURRENT_EVENT);
      } else {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.noEventsPhrase = 'eventList.noEvents';
      }
      return events;
    },
  },
  methods: {
    showTour() {
      if (!this.tourStates.mainTour && !this.areAllToursFinished && this.breakpointLg.matches) {
        setTimeout(() => {
          productTours[this.$i18n.locale].mainTour.start();
        }, 350);
      }
    },
    onEventSearch(search) {
      this.searchEvent = search;
    },
    loadEvent(id, doCloseSidebar = true) {
      if ((this.event.id !== id && this.$store.state.events.eventStatus === 'loading')
        || (this.$store.state.events.eventStatus === 'cleared')) {
        Http.abortRequest();
        this.$store.commit(FETCH_EVENT_REQUEST);
      }
      // if (this.event.id !== id) {
      const link = this.linkToEvents(id);
      const newEvent = this.events.find((event) => event.id === id);
      this.$store.commit(SET_CURRENT_EVENT, newEvent);
      this.$store.commit(SET_EVENT_CONTACTS, newEvent.contacts);
      if (doCloseSidebar) this.$refs.eventsSidebar.hide();
      if (link) {
        this.$router.push(link);
      }
      // }
    },
    onDeleteEvent(event) {
      this.$bvModal.msgBoxConfirm(this.$t('CP.confirmDeleteEvent'), {
        title: event.event_name,
        buttonSize: 'md',
        okVariant: 'danger',
        okTitle: this.$t('CP.yes'),
        cancelTitle: this.$t('CP.cancel'),
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      })
        .then((isConfirmed) => {
          if (isConfirmed) this.$store.dispatch(DELETE_EVENT_REQUEST, event.id);
        });
    },
    linkToEvents(id) {
      let link = '/events';
      if (+id === +this.$route.params.id) return false;
      if (id) {
        link += `/${id}`;
      }
      if (!this.searchEvent) {
        link += '/transactions';
      } else if (this.$route.path.indexOf('transactions') !== -1) {
        link += '/transactions';
      } else if (this.$route.path.indexOf('my-share') !== -1) {
        link += '/my-share';
      } else if (this.$route.path.indexOf('debts') !== -1) {
        link += '/debts';
      } else {
        link += '/transactions';
      }
      return link;
    },
  },
};
</script>

<style lang="scss" scoped>
.events-container {
  height: 100%;
  flex: 0 1 372px;
}

.events-sidebar {
  display: none;
}

.events-aside {
  box-sizing: border-box;
  padding: 20px 30px;
  position: relative;
  color: var(--text-main-color);
  background-color: var(--backgound-color);
  max-width: 372px;
  /*min-height: 100%;*/
  height: 100%;
  box-shadow: rgba(0, 76, 165, 0.16) 1px 1px 4px;
}

.error-message {
  color: var(--error-red);
  height: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.event-list {
  max-height: calc(100% - 36px - 8px - 36px - 16px - 30px);
  min-height: 120px;
  overflow-y: auto;
  padding: 0 20px 20px;
  margin: 0 -20px;
  /*-ms-overflow-style: none;  !* IE and Edge *!*/
  /*scrollbar-width: none;  !* Firefox *!*/
  /*&::-webkit-scrollbar {*/
  /*  display: none; !* Chrome, Safari and Opera *!*/
  /*}*/
}

.events-aside-navigation {
  margin: -20px -30px 0;
  padding: 20px 30px 0;
  background: #EDF1F9;
  height: 163px;
}

.events-aside__title-add {
  margin: 0 0 8px;
}

.sort-list {
  height: 30px;
}

@media screen and (min-width: $breakpoint-sm) and (max-width: $breakpoint-lg) {
  .events-container {
    flex: 0 1 0;
  }
  .events-aside {
    display: none;
  }
  .events-aside_sidebar {
    box-sizing: border-box;
    padding: 20px 30px;
    position: relative;
    color: var(--text-main-color);
    background-color: var(--backgound-color);
    flex: 0 1 372px;
    min-height: 355px;
    height: var(--height-full);
    box-shadow: rgba(0, 76, 165, 0.16) 1px 1px 4px;
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .events-container {
    display: flex;
    flex: 1;
    min-width: unset;
  }
  .events-aside {
    flex: 1 0 100%;
    max-width: none;
    height: unset;
    /*min-height: 100%;*/
  }
}
</style>
